import { api, ApiError } from "../../api";
import type { TargetPointResultsAttach } from "../../api/client";
import type { ErrDetail } from "../../api/unwrap";
import type { PointResultRow } from "../../lib/resultProperties";
import { partialResultsNotice } from "../work-orders/labResults";

export const TARGET_RESULTS_MAX_BYTES = 2 * 1024 * 1024;
export const TARGET_RESULTS_MAX_ROWS = 5_000;

const MISSING_SHOWN = 12;
const PROCESSING =
  "Processing — the order will show Complete when the results finish importing.";
const UPLOADED =
  "Uploaded — the results are attaching to this layer’s sampling event now.";
const RETRY =
  "Results for this layer are being saved. Wait a moment and try again.";

export function pointResultsOverCap(bytes: number, rows: number): boolean {
  return bytes > TARGET_RESULTS_MAX_BYTES || rows > TARGET_RESULTS_MAX_ROWS;
}

/*
 * A 413, or a missing route during a split deploy, still uses the signed
 * upload. A 503 must not: the other request is in the same lock.
 * An unregistered path is `ERR_NOT_FOUND` / "Not Found". A missing set uses
 * a different message and stays on this screen.
 */
export function pointResultsFallsBack(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 413 || error.code === "target_results_too_large")
    return true;
  if (error.code === "route_not_found") return true;
  return (
    error.status === 404 &&
    error.code === "ERR_NOT_FOUND" &&
    error.message === "Not Found"
  );
}

function nameList(names: string[], count: number, show = names.length): string {
  const shown = names.slice(0, show);
  const hidden = Math.max(count, names.length) - shown.length;
  const extra = hidden > 0 ? ` … (+${hidden})` : "";
  return shown.join(", ") + extra;
}

function namesFromDetails(details: ErrDetail[] | undefined): string | null {
  for (const row of details ?? []) {
    const ids = Array.isArray(row.ids)
      ? row.ids.map(String)
      : Array.isArray(row.rows)
        ? row.rows.map(String)
        : null;
    if (ids && ids.length) {
      const count = typeof row.count === "number" ? row.count : ids.length;
      return nameList(ids, count);
    }
    if (row.field === "ids" && row.message) return row.message;
  }
  return null;
}

function namedRefusal(error: ApiError, sentence: string): string {
  const names = namesFromDetails(error.details);
  return names ? `${sentence}: ${names}. Nothing was attached.` : error.message;
}

export function pointResultsFailure(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 503 || error.code === "target_results_busy")
    return RETRY;
  switch (error.code) {
    case "target_results_unmatched":
      return namedRefusal(error, "These IDs are not on this layer");
    case "target_results_duplicate_ids":
      return namedRefusal(
        error,
        "These IDs repeat in the file or match more than one point",
      );
    case "target_results_blank_ids":
      return namedRefusal(error, "These rows have a blank ID");
    case "target_results_no_id_column":
      return "No sample ID column was recognised. Nothing was attached.";
    case "target_results_no_values":
      return namedRefusal(error, "These IDs have no recognised values");
    default:
      return null;
  }
}

type OrderFlag = { status?: string; prior_status?: string };

function orderFlag(
  order: TargetPointResultsAttach["work_order"],
): OrderFlag | null {
  if (!order) return null;
  return { status: order.status, prior_status: order.prior_status };
}

function orderWasCanceled(order: OrderFlag | null): boolean {
  const prior = (order?.prior_status ?? "").toLowerCase();
  const status = (order?.status ?? "").toLowerCase();
  return prior.includes("cancel") || status.includes("cancel");
}

function orderSentence(
  hadWorkOrder: boolean,
  order: OrderFlag | null,
): string | null {
  if (!hadWorkOrder) return null;
  if (orderWasCanceled(order)) return "Order was canceled; results saved.";
  if (!order) return null;
  const status = (order.status ?? "").toLowerCase();
  if (status === "completed" || status === "complete")
    return "Order marked Complete.";
  return null;
}

function missingNotice(missing: string[]): string | null {
  if (!missing.length) return null;
  const noun = missing.length === 1 ? "point" : "points";
  return `${missing.length} ${noun} had no result: ${nameList(missing, missing.length, MISSING_SHOWN)}.`;
}

export function pointAttachCopy(input: {
  title: string;
  attached: number;
  hadWorkOrder: boolean;
  workOrder: TargetPointResultsAttach["work_order"];
  missing: string[];
  asyncFallback: boolean;
  partial: string | null;
}): { status: string; notice: string | null } {
  if (input.asyncFallback) {
    return {
      status: input.hadWorkOrder ? PROCESSING : UPLOADED,
      notice: null,
    };
  }
  const order = orderSentence(input.hadWorkOrder, orderFlag(input.workOrder));
  const status = [`Attached ${input.attached} results to ${input.title}.`];
  if (order) status.push(order);
  const gap = missingNotice(input.missing);
  const notice = [gap, input.partial].filter(Boolean).join(" ") || null;
  return { status: status.join(" "), notice };
}

function readMissing(body: TargetPointResultsAttach): string[] {
  return Array.isArray(body.missing) ? body.missing.map(String) : [];
}

export async function uploadPointLayerResults(args: {
  fieldId: string;
  setId: string;
  file: File;
  rowCount: number;
  title: string;
  workOrderId?: string;
  rows: PointResultRow[];
}): Promise<{ status: string; notice: string | null; asyncFallback: boolean }> {
  const hadWorkOrder = Boolean(args.workOrderId);
  if (!pointResultsOverCap(args.file.size, args.rowCount)) {
    try {
      const body = await api.attachTargetPointResultRows(
        args.fieldId,
        args.setId,
        args.rows,
        args.workOrderId ? { workOrderId: args.workOrderId } : undefined,
      );
      const copy = pointAttachCopy({
        title: args.title,
        attached: typeof body.attached === "number" ? body.attached : 0,
        hadWorkOrder,
        workOrder: body.work_order,
        missing: readMissing(body),
        asyncFallback: false,
        partial: partialResultsNotice(body),
      });
      return { ...copy, asyncFallback: false };
    } catch (error) {
      if (!pointResultsFallsBack(error)) throw error;
    }
  }
  await api.uploadFile(args.fieldId, "soil-samples", args.file, {
    sample_event_id: `points:${args.setId}`,
    ...(args.workOrderId ? { work_order_id: args.workOrderId } : {}),
  });
  const copy = pointAttachCopy({
    title: args.title,
    attached: 0,
    hadWorkOrder,
    workOrder: null,
    missing: [],
    asyncFallback: true,
    partial: null,
  });
  return { ...copy, asyncFallback: true };
}
